/*jshint esversion: 6 */
import React from 'react';
import { connect } from 'react-redux';
import * as constants from 'app/redux/constants.js';
import * as _ from 'underscore';
import { SocialActions } from 'app/redux/actions.js';

function mapStateToProps(state) {
    return({
        socials: state.socials
    });
}

function mapDispatchToProps(dispatch) {
    return {
        getSocials: (force) => {
            dispatch(SocialActions.fetchSocials(force));
        },
        getSocial: (id) => {
            dispatch(SocialActions.fetchSocial(id));
        },
        updateSocial: (social) => {
            dispatch(SocialActions.updateSocial(social));
        },
        deleteSocial: (id) => {
            dispatch(SocialActions.deleteSocial(id));
        },
        createSocial: (social) => {
            dispatch(SocialActions.createSocial(social));
        },
        getSocialLog: (id) => {
            dispatch(SocialActions.fetchSocialLog(id));
        },
        checkAuth: (id) => {
            dispatch(SocialActions.checkSocialAuth(id));
        },
        getSocialTypes: () => {
            dispatch(SocialActions.fetchSocialTypes());
        },
        getSocialTypeDefaults: (id) => {
            dispatch(SocialActions.fetchSocialTypeDefaults(id));
        },
        getQueueCount: (id) => {
            dispatch(SocialActions.fetchQueueCount(id));
        },
        getFacebookGroupStatus: (data) => {
            dispatch(SocialActions.fetchFacebookGroupStatus(data));
        },
        createBatchSocials: (data) => {
            dispatch(SocialActions.createBatchSocials(data));
        },
        createBatchPoll: (data) => {
            dispatch(SocialActions.createBatchPoll(data));
        },
        getYouTubeCategories: (id) => {
            dispatch(SocialActions.fetchYouTubeCategories(id));
        },
        getYouTubePlaylists: (id) => {
            dispatch(SocialActions.fetchYouTubePlaylists(id));
        },
        getPostAnalytics: (data) => {
            dispatch(SocialActions.fetchSocialPostAnalytics(data));
        }
	};
}

export default function SocialProvider(Component) {

    class SocialProvider extends React.Component {
        constructor(props) {
            super(props);

            /*
             * A ref, not state: register/unregister run from consumers' mount and
             * unmount, several in the same commit, and a setState-based list drops
             * every entry but the last of that batch. Nothing here is rendered, so
             * there is nothing for a re-render to do either.
             */
            this.callbacks = [];

            this.findSocial = this.findSocial.bind(this);
            this.isMovementRestricted = this.isMovementRestricted.bind(this);
            this.isDuplicateSocial = this.isDuplicateSocial.bind(this);
            this.isBlacklisted = this.isBlacklisted.bind(this);
            this.isOverTheLimit = this.isOverTheLimit.bind(this);
            this.getErrorForSocial = this.getErrorForSocial.bind(this);
            this.registerCallback = this.registerCallback.bind(this);
            this.unregisterCallback = this.unregisterCallback.bind(this);
            this.changedSocials = this.changedSocials.bind(this);

        }

        /*
         * One place to notice that a social request has come back, so the
         * components that care about the answer don't each have to watch the
         * loading flags for themselves.
         *
         * The watched set is whatever has been registered for rather than a fixed
         * list, so anything keyed in `app.loading` is available — the list and
         * show calls as much as the three writes.
         *
         * The actions are the redux ones (`app/redux/constants.js`), which is
         * also what keys `app.loading`. They are NOT the server-side constants on
         * `app.config.constants` — that payload carries SOCIAL_CREATE only as an
         * endpoint path, so matching against it never fires.
         */
        componentDidUpdate(prevProps) {
            let settled = _.uniq(this.callbacks.map((callback) => callback.action))
                .filter((action) => {
                    return prevProps.app.loading[action] === true && this.props.app.loading[action] === false;
                });

            settled.map((action) => {
                let changed = this.changedSocials(action, prevProps.socials.list, this.props.socials.list),
                    before = {},
                    detail = null;

                prevProps.socials.list.map((social) => { before[social.id] = social; });

                detail = {
                    action: action,
                    social: changed.length ? changed[0] : null,
                    changed: changed,
                    before: before
                };

                // filter() snapshots, so a callback that unregisters itself while
                // this runs doesn't disturb the rest of the pass.
                this.callbacks
                    .filter((callback) => callback.action === action)
                    .map((callback) => {
                        callback.func(detail);
                    });
            });

        }

        /*
         * Which socials a settled request was about.
         *
         * The action's payload would say outright, but a connected component sees
         * state and not actions, and the socials reducer deep-clones on every
         * action — so object identity says nothing here and the answer has to come
         * from comparing the two lists.
         *
         * Exact for creates and deletes, which are an id-set difference. An update
         * is found by value for the same reason: the record the reducer replaced
         * with the response no longer matches its previous self, while every other
         * record is a clone that does. An update that changed nothing reports
         * nothing, which no caller can tell apart from an update it didn't make.
         *
         * Every other action reports nothing rather than guessing: a list refresh
         * has no one social it is about, and saying otherwise would be a lie a
         * caller could act on.
         */
        changedSocials(action, prevList, nextList) {
            let prevById = {},
                nextById = {};

            prevList.map((social) => { prevById[social.id] = social; });
            nextList.map((social) => { nextById[social.id] = social; });

            if (action === constants.SOCIAL_CREATE) {
                return nextList.filter((social) => prevById[social.id] === undefined);
            }

            if (action === constants.SOCIAL_DELETE) {
                return prevList.filter((social) => nextById[social.id] === undefined);
            }

            if (action === constants.SOCIAL_UPDATE) {
                return nextList.filter((social) => {
                    return prevById[social.id] !== undefined && !_.isEqual(prevById[social.id], social);
                });
            }

            return [];

        }

        findSocial(id) {
            let social = null;

            for (let i = 0; i < this.props.socials.list.length; ++i) {
                if (parseInt(this.props.socials.list[i].id, 10) === parseInt(id, 10)) {
                    social = this.props.socials.list[i];
                    break;
                }
            }

            return social;

        }

        isMovementRestricted() {
            let errors = [];

            errors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

            if (errors.length) {
                for (let i = 0; i < errors.length; ++i) {
                    if (errors[i].errors.perms !== undefined && errors[i].errors.perms.code === 2028) {
                        return true;
                    }
                }
            }

            return false;
        }

        isDuplicateSocial() {
            let errors = [];

            errors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

            if (errors.length) {
                for (let i = 0; i < errors.length; ++i) {
                    if (errors[i].errors.perms !== undefined && errors[i].errors.perms.code === 2012) {
                        return true;
                    }
                }
            }

            return false;

        }

        isBlacklisted() {
            let errors = [];

            errors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

            if (errors.length) {
                for (let i = 0; i < errors.length; ++i) {
                    if (errors[i].errors !== undefined &&
                        errors[i].errors.creds !== undefined &&
                        errors[i].errors.creds === "Social is not allowed") {
                        return true;
                    }
                }
            }

            return false;
        }

        isOverTheLimit() {
            let errors = [];

            errors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

            if (errors.length) {
                for (let i = 0; i < errors.length; ++i) {
                    if (errors[i].errors.perms !== undefined && Number.isInteger(errors[i].errors.perms.code) && [2012, 2028].indexOf(errors[i].errors.perms.code) === -1) {
                        return true;
                    }
                }
            }

            return false;
        }

        getErrorForSocial(id) {
            let errors = [];

            errors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

            for (let i = 0; i < errors.length; ++i) {
				if (errors[i].errors[id]) {
					return errors[i].errors;
				}
			}

            return null;
        }

        /*
         * Ask to be told when a social request settles.
         *
         *   registerCallback({action, func})
         *
         * `action` is a redux action type from `app/redux/constants.js`; `func` is
         * called as func(detail) once `app.loading[action]` goes back to
         * false, whether the request succeeded or failed — a caller that only
         * wants the successful case checks getApiErrors, exactly as the
         * componentDidUpdate it is replacing did.
         *
         *   detail.action   the action that settled
         *   detail.social   the one social it was about, or null
         *   detail.changed  every social it was about; detail.social is the first
         *   detail.before   every social as it was beforehand, keyed by id, for a
         *                   caller that needs the transition and not just the new
         *                   value — `detail.before[social.id]`
         *
         * Register on mount and unregister on unmount with the SAME descriptor
         * object, so the two match by identity.
         */
        registerCallback(callback) {
            // check for correct shape
            if ((callback.action === undefined || !callback.action) ||
                (callback.func === undefined || typeof callback.func !== "function")) {
                return;
            }

            this.callbacks = this.callbacks.concat([callback]);

        }

        /*
         * Both the action and the function have to match to drop an entry: two
         * components watching the same action share an action, and one component
         * watching two actions shares a function, so either test on its own
         * unregisters somebody else's callback.
         */
        unregisterCallback(callback) {
            this.callbacks = this.callbacks.filter((existingCallback) => {
                return !(callback.action === existingCallback.action &&
                    callback.func === existingCallback.func);
            });

        }

        render() {
            return (
                <Component
                    {...this.props}
                    findSocial={this.findSocial}
                    isMovementRestricted={this.isMovementRestricted}
                    isDuplicateSocial={this.isDuplicateSocial}
                    isBlacklisted={this.isBlacklisted}
                    isOverTheLimit={this.isOverTheLimit}
                    getErrorForSocial={this.getErrorForSocial}
                    registerCallback={this.registerCallback}
                    unregisterCallback={this.unregisterCallback}/>
            );
        }
    }

    SocialProvider.displayName = "SocialProvider";

    return connect(mapStateToProps, mapDispatchToProps)(SocialProvider);
};
